import { useEffect, useState } from 'react';
import Select, { InputActionMeta } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { shouldForwardTypeaheadInput } from 'utils/typeahead-input';

export type TypeaheadOption = {
  label: string;
  value: string | number;
  // Listed but not selectable (react-select honors `isDisabled` on the
  // option itself) — e.g. a phone number that cannot receive texts, shown
  // in red with the reason in its label.
  isDisabled?: boolean;
};

type TypeaheadProps = {
  options: string[] | TypeaheadOption[];
  onChange: (value: string | number) => void;
  inputValue?: string;
  onInputChange?: (input: string) => void;
  placeholder?: string;
  disabled?: boolean;
  allowCustom?: boolean;
  customOptionFormat?: (input: string) => string;
};

const optionClassName = ({ isDisabled }: { isDisabled: boolean }) =>
  isDisabled ? '!cursor-not-allowed !text-red-600' : '!cursor-pointer';

export default function Typeahead({
  options,
  onChange,
  inputValue,
  onInputChange,
  placeholder,
  disabled,
  allowCustom,
  customOptionFormat
}: TypeaheadProps) {
  const [formattedOptions, setFormattedOptions] = useState<TypeaheadOption[]>(
    []
  );

  const handleInputChange = (input: string, { action }: InputActionMeta) => {
    if (onInputChange && shouldForwardTypeaheadInput(action)) {
      onInputChange(input);
    }
  };

  useEffect(() => {
    if (options) {
      if (typeof options[0] === 'string') {
        setFormattedOptions(
          (options as string[]).map((option) => ({
            label: option,
            value: option
          }))
        );
      } else {
        setFormattedOptions(options as TypeaheadOption[]);
      }
    }
  }, [options]);

  return allowCustom ? (
    <CreatableSelect
      className='[&_input]:!ring-0'
      classNames={{
        input: () => '!cursor-text',
        dropdownIndicator: () => '!cursor-pointer',
        clearIndicator: () => '!cursor-pointer',
        option: optionClassName
      }}
      placeholder={placeholder}
      options={formattedOptions}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={(option) => onChange(option?.value ?? '')}
      formatCreateLabel={customOptionFormat}
      isDisabled={disabled}
      openMenuOnFocus
      openMenuOnClick
      isClearable
    />
  ) : (
    <Select
      className='[&_input]:!ring-0'
      classNames={{
        input: () => '!cursor-text',
        dropdownIndicator: () => '!cursor-pointer',
        clearIndicator: () => '!cursor-pointer',
        option: optionClassName
      }}
      placeholder={placeholder}
      options={formattedOptions}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={(option) => onChange(option?.value ?? '')}
      isDisabled={disabled}
      openMenuOnFocus
      openMenuOnClick
      isClearable
    />
  );
}
